import React from 'react';

import { Stack, Text, Group, NumberInput, Select, Button, TextInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import useEditExpectation from 'hooks/reports/utilization/package-utilization/useEditExpectation';
import type { PackageUtilizationExpectationListItem } from 'types';
import notificationHandler from 'utils/notifications';

import { useModal } from '../../Context/ModalContext';
import AttendanceHelpButton from '../Components/AttendanceHelpButton';

interface FormValues {
  frequencyValue: number;
  frequencyUnit: 'week' | 'month' | 'year';
}

type FormProps = {
  className?: string;
  packageExpectation: PackageUtilizationExpectationListItem;
};

const Form = ({ className, packageExpectation }: FormProps): JSX.Element => {
  const { openModal } = useModal();
  const { mutate: editExpectation, isLoading } = useEditExpectation();
  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    validate: {
      frequencyValue: isNotEmpty('Please enter the number of times'),
      frequencyUnit: isNotEmpty('Please select a time period'),
    },
    initialValues: {
      frequencyValue: parseFloat(packageExpectation.frequencyValue),
      frequencyUnit: packageExpectation.frequencyUnit,
    },
  });

  const handleSubmit = (values: FormValues): void => {
    editExpectation(
      {
        expectationId: packageExpectation.packageUtilizationExpectationId,
        expectation: {
          frequencyUnit: values.frequencyUnit,
          frequencyValue: values.frequencyValue.toString(),
        },
      },
      {
        onSuccess: () => {
          openModal('list');
          successNotification('The package has been edited successfully.');
        },
        onError: error => {
          errorNotification('The package could not be edited.', error.response?.data.message);
        },
      },
    );
  };

  return (
    <form className={className} onSubmit={form.onSubmit(handleSubmit)}>
      <Stack spacing='md'>
        <TextInput
          size='md'
          label='Package to edit'
          readOnly
          variant='unstyled'
          value={packageExpectation.package.name}
        />

        <Group align='center' spacing='xs'>
          <Text size='md'>Define attendance expectation</Text>
          <AttendanceHelpButton position='bottom' withArrow withinPortal width={300} />
        </Group>

        <Group align='start' position='apart'>
          <Group align='start'>
            <NumberInput
              placeholder='Enter times'
              required
              min={0.01}
              step={1}
              precision={2}
              {...form.getInputProps('frequencyValue')}
            />
            <Text size='sm' pt='0.5rem'>
              times per
            </Text>
            <Select
              placeholder='Select time'
              data={['week', 'month', 'year']}
              required
              clearable
              {...form.getInputProps('frequencyUnit')}
            />
          </Group>
          <Group spacing='xs'>
            <Button variant='filled' color='red' onClick={() => openModal('list')}>
              Cancel
            </Button>
            <Button variant='filled' type='submit' disabled={!form.isDirty()} loading={isLoading}>
              Save
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
};

export default Form;
